export const supportedLangs = ["en", "nl"] as const;
export type XoLang = typeof supportedLangs[number];
export const defaultLang: XoLang = "en";

const fallbackOrigin = "https://xo-call.com";
const demoRoute = "/demo";
const callRoute = "/call";
const callLandingRoute = "/call/landing";
const ledgerRecordRoutePrefix = "/ledger/record";

const recordRoutePrefix = "/record/call";
const trustLoopRoute = "/trust-loop";
const trustLoopCardRoute = "/trust-loop/card";

type XoUrlParamValue = string | number | boolean | null | undefined;
export type XoUrlParams = URLSearchParams | Record<string, XoUrlParamValue>;
export type XoDemoUrlOptions = {
  params?: XoUrlParams;
  hash?: string;
  origin?: string | null;
  absolute?: boolean;
};

type XoBuildInput = XoUrlParams | XoDemoUrlOptions;

function cleanOrigin(value: unknown): string | null {
  const raw = typeof value === "string" ? value.trim().replace(/\/+$/, "") : "";
  return /^https?:\/\//i.test(raw) ? raw : null;
}

function browserOrigin(): string | null {
  if (typeof window === "undefined") return null;
  return cleanOrigin(window.location.origin);
}

export function currentXoDemoOrigin(): string {
  return browserOrigin() ?? fallbackOrigin;
}

function parseParams(searchParams: URLSearchParams | string | null | undefined): URLSearchParams {
  if (searchParams instanceof URLSearchParams) return searchParams;
  const raw = String(searchParams ?? "");
  return new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
}

export function getXoLang(searchParams: URLSearchParams | string | null | undefined): XoLang {
  return parseParams(searchParams).get("lang") === "nl" ? "nl" : defaultLang;
}

export function getXoDemoMode(searchParams: URLSearchParams | string | null | undefined): boolean {
  const demo = parseParams(searchParams).get("demo");
  return demo === "1" || demo === "true";
}

export function xoDemoModeParams(active: boolean): Record<string, string | null> {
  return { demo: active ? "1" : null };
}

export function nextXoLang(lang: XoLang): XoLang {
  return lang === "nl" ? "en" : "nl";
}

function isOptions(input: XoBuildInput | undefined): input is XoDemoUrlOptions {
  return Boolean(
    input
    && !(input instanceof URLSearchParams)
    && typeof input === "object"
    && ("params" in input || "hash" in input || "origin" in input || "absolute" in input),
  );
}

function normalizeOptions(input?: XoBuildInput): XoDemoUrlOptions {
  if (!input) return {};
  return isOptions(input) ? input : { params: input };
}

function paramsEntries(params?: XoUrlParams): [string, string][] {
  if (!params) return [];
  const entries = params instanceof URLSearchParams ? Array.from(params.entries()) : Object.entries(params);
  return entries
    .filter(([, value]) => String(value ?? "").trim().length > 0)
    .map(([key, value]) => [key, String(value)]);
}

function splitUrl(url: string): { base: string; query: string; hash: string } {
  const hashIndex = url.indexOf("#");
  const beforeHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const queryIndex = beforeHash.indexOf("?");
  if (queryIndex < 0) return { base: beforeHash, query: "", hash };
  return { base: beforeHash.slice(0, queryIndex), query: beforeHash.slice(queryIndex + 1), hash };
}

function mergeUrl(url: string, lang: XoLang, params?: XoUrlParams, hash?: string): string {
  if (/^https?:\/\//i.test(url)) {
    const parsed = new URL(url);
    for (const [key, value] of paramsEntries(params)) parsed.searchParams.set(key, value);
    if (lang === defaultLang) parsed.searchParams.delete("lang");
    else parsed.searchParams.set("lang", lang);
    if (hash != null) parsed.hash = hash ? (hash.startsWith("#") ? hash.slice(1) : hash) : "";
    return parsed.toString();
  }

  const parts = splitUrl(url);
  const search = new URLSearchParams(parts.query);
  for (const [key, value] of paramsEntries(params)) search.set(key, value);
  if (lang === defaultLang) search.delete("lang");
  else search.set("lang", lang);
  const cleanHash = hash == null ? parts.hash : hash ? (hash.startsWith("#") ? hash : `#${hash}`) : "";
  const query = search.toString();
  return `${parts.base}${query ? `?${query}` : ""}${cleanHash}`;
}

export function withXoLang(url: string, lang: XoLang): string {
  return mergeUrl(url, lang);
}

export function toggleLangUrl(currentUrl: string, targetLang: XoLang): string {
  return withXoLang(currentUrl, targetLang);
}

function withOptionalOrigin(path: string, options: XoDemoUrlOptions): string {
  const explicitOrigin = cleanOrigin(options.origin);
  if (explicitOrigin) return `${explicitOrigin}${path.startsWith("/") ? path : `/${path}`}`;
  if (options.absolute) {
    const origin = currentXoDemoOrigin();
    return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
  }
  return path;
}

function buildRoute(route: string, lang: XoLang, input?: XoBuildInput): string {
  const options = normalizeOptions(input);
  const path = mergeUrl(route, lang, options.params, options.hash);
  return withOptionalOrigin(path, options);
}

export function buildDemoUrl(lang: XoLang, params?: XoBuildInput): string {
  return buildRoute(demoRoute, lang, params);
}

export function buildCallUrl(lang: XoLang, params?: XoBuildInput): string {
  return buildRoute(callRoute, lang, params);
}

export function buildCallLandingUrl(lang: XoLang, params?: XoBuildInput): string {
  return buildRoute(callLandingRoute, lang, params);
}


export function buildLedgerRecordUrl(ledgerRecordId: string | null | undefined, lang: XoLang, input?: XoBuildInput): string {
  const trimmed = String(ledgerRecordId ?? "").trim();
  const route = trimmed ? `${ledgerRecordRoutePrefix}/${encodeURIComponent(trimmed)}` : ledgerRecordRoutePrefix;
  return buildRoute(route, lang, input);
}

export function buildAbsoluteLedgerRecordUrl(ledgerRecordId: string | null | undefined, lang: XoLang, input?: XoBuildInput): string {
  const merged = normalizeOptions(input);
  return buildLedgerRecordUrl(ledgerRecordId, lang, { ...merged, absolute: true });
}

export function buildRecordUrl(callId: string | null | undefined, lang: XoLang, params?: XoBuildInput): string {
  const trimmed = String(callId ?? "").trim();
  const route = trimmed ? `${recordRoutePrefix}/${encodeURIComponent(trimmed)}` : recordRoutePrefix;
  return buildRoute(route, lang, params);
}

export function buildAbsoluteRecordUrl(callId: string | null | undefined, lang: XoLang): string {
  const demo = typeof window !== "undefined" && getXoDemoMode(window.location.search);
  return buildRecordUrl(callId, lang, { params: demo ? { demo: "1" } : undefined, absolute: true });
}

export function buildAbsoluteCallUrl(roomId: string | null | undefined, lang: XoLang): string {
  const room = String(roomId ?? "").trim();
  const demo = typeof window !== "undefined" && getXoDemoMode(window.location.search);
  return buildCallUrl(lang, {
    params: { room: room || null, join: room ? "1" : null, demo: demo ? "1" : null },
    absolute: true,
  });
}

export function buildAbsoluteProofUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const origin = currentXoDemoOrigin();
  return `${origin}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

export function buildTrustLoopUrl(lang: XoLang, params?: XoBuildInput): string {
  return buildRoute(trustLoopRoute, lang, params);
}

export function buildTrustLoopCardUrl(lang: XoLang, params?: XoBuildInput): string {
  return buildRoute(trustLoopCardRoute, lang, params);
}
